import classNames from 'classnames';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { Pin } from 'components/Icons';
import Label from 'components/Label';
import ProgressBar from 'components/ProgressBar';
import ProjectBadge from 'components/projects/ProjectBadge';
import Tooltip from 'components/Tooltip';
import type { ProjectDropdownOption } from 'factories/projects';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import styles from './SelectOption.module.scss';

/**
 * Props for the SelectOption component.
 *
 * @interface Props
 */
interface Props {
  /** The project dropdown option to render. Contains project details, color, label, and metadata. */
  option: ProjectDropdownOption;
  /** Optional CSS class name to apply to the option container. */
  className?: string;
  /** Whether this option is currently selected in the dropdown. */
  isSelected?: boolean;
  /**
   * Base path for converting options into clickable links.
   * When provided, the option will be rendered as a Link component instead of a div,
   * with the href set to `${optionAsLinkFromBasePath}${option.value}`.
   * For example, if set to "/projects/", the link will be "/projects/123" for option.value = 123.
   */
  optionAsLinkFromBasePath?: string;
}

class SelectOption extends Component<Props> {
  render() {
    const { option, optionAsLinkFromBasePath } = this.props;
    const className = classNames(styles.optionValue, this.props.className);

    if (option.createTrigger) {
      return (
        <div className={className}>
          <div className={styles.optionColor}>
            <div className={styles.icon}>+&nbsp;</div>
          </div>
          <div className={styles.label} data-testid="select_option">
            {option.label}
          </div>
        </div>
      );
    }

    if (!option.color) {
      return <div className={classNames(className, 'fs-mask')}>{option.label}</div>;
    }

    // Recent projects aren't nested by client, so include client name too:
    const showClient = !!option.showClient && !!option.client;
    const activeForecast = !!option.forecastId;
    const showProgress = option.budgetSpendInPercent || activeForecast;
    const budgetIsPending = !!option.budgetIsPending;

    const budgetTooltip = budgetIsPending
      ? 'Budget is being calculated, please wait...'
      : activeForecast
        ? `Logged ${option.budgetSpendInPercent?.toFixed(2)}% of estimate`
        : option.budgetSpendInPercent && option.budgetSpendInPercent > 100
          ? `${(option.budgetSpendInPercent - 100).toFixed(2)}% over budget`
          : `Spent ${option.budgetSpendInPercent?.toFixed(2) || ''}% of budget`;

    const child = (
      <React.Fragment>
        <div
          className={classNames(styles.optionIndicator, { [styles.isForecast]: activeForecast })}
          data-tic-mapped={option.project?.tic ? 'true' : undefined}
          data-tic-tool-id={option.project?.tic?.tool_id}
        >
          <ProjectBadge
            color={`#${option.color}`}
            icon={activeForecast ? 'task' : undefined}
            size="small"
            ticMeta={option.project?.tic}
          />
        </div>
        <div className={classNames(styles.label, 'fs-mask')}>
          {option.value != null && <DiagnosticResourceId resource="project" id={option.value} />}
          {option.label}
          {showClient && (
            <small>
              &nbsp; (<DiagnosticResourceId resource="client" id={option.clientId!} />
              {option.client})
            </small>
          )}
        </div>
        {option.isPinned && (
          <span className={styles.pin}>
            <Pin />
          </span>
        )}
        {option.isArchived && (
          <div>
            <Label size="small">Archived</Label>
          </div>
        )}
        {/* todo: make this an option from the ProjectSearch component */}
        {/*
         * ProjectsRevamp lost functionality - the projects are generally
         * light, so they won't have this until picked
         */}
        {showProgress ? (
          <Tooltip position="left" title={budgetTooltip} className={styles.progressWrapper}>
            {option.billable && <small>{option.currencySymbol}</small>}
            <ProgressBar
              value={budgetIsPending ? 0 : option.budgetSpendInPercent || 0}
              secondaryValue={budgetIsPending ? 100 : undefined}
              secondaryProgressColor="#b4b9bf"
              size="xsmall"
            />
          </Tooltip>
        ) : null}
      </React.Fragment>
    );

    if (optionAsLinkFromBasePath) {
      const linkTo = `${optionAsLinkFromBasePath}${option.value}`;
      return (
        <Link
          className={className}
          to={linkTo}
          onClick={e => {
            // Prevent the dropdown from blurring in the case of cmd+click
            // blurring clears the filter string, which is annoying
            e.stopPropagation();
          }}
        >
          {child}
        </Link>
      );
    }

    return <div className={className}>{child}</div>;
  }
}

export default SelectOption;
